import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CalendarClock, CheckCircle2, Scale } from "lucide-react";
import { type FormEvent, useState } from "react";
import { api, jsonBody } from "../../api/client";
import type {
  AccountingPeriod,
  Dispute,
  ListResponse,
  ReconciliationException,
} from "../../api/types";
import { useSensitiveAction } from "../../auth/SensitiveActionGate";
import { formatDate, formatMoney, titleCase } from "../../lib/format";
import {
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Modal,
  Notice,
  ReasonDialog,
  StatusPill,
} from "../../ui/components";

type FinanceAction =
  | {
      kind: "dispute";
      item: Dispute;
      status: "needs_response" | "under_review" | "won" | "lost" | "closed";
    }
  | {
      kind: "exception";
      item: ReconciliationException;
      status: "open" | "investigating" | "resolved" | "ignored";
    }
  | { kind: "period"; item: AccountingPeriod; status: "closed" };

interface FinanceOperationsProps {
  canManageFinance: boolean;
  canRecordPayments: boolean;
  canManagePeriods: boolean;
}

export function FinanceOperations({
  canManageFinance,
  canRecordPayments,
  canManagePeriods,
}: FinanceOperationsProps) {
  const sensitive = useSensitiveAction({
    setupTitle: "Protect finance changes with two-step verification",
    lockedTitle: "Confirm your identity to change finance records",
    lockedDescription:
      "These changes affect payment and accounting records. Enter a fresh code before continuing.",
  });
  const disputes = useQuery({
    queryKey: ["finance", "disputes"],
    queryFn: () => api<ListResponse<Dispute>>("/api/v1/finance/disputes"),
    enabled: canManageFinance,
  });
  const exceptions = useQuery({
    queryKey: ["finance", "reconciliation-exceptions"],
    queryFn: () =>
      api<ListResponse<ReconciliationException>>("/api/v1/finance/reconciliation-exceptions"),
    enabled: canRecordPayments,
  });
  const periods = useQuery({
    queryKey: ["finance", "accounting-periods"],
    queryFn: () => api<ListResponse<AccountingPeriod>>("/api/v1/finance/accounting-periods"),
    enabled: canManagePeriods,
  });
  const [action, setAction] = useState<FinanceAction>();
  const [periodOpen, setPeriodOpen] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();

  if (!canManageFinance && !canRecordPayments) return null;

  async function transition(reason: string) {
    if (!action) return;
    setWorking(true);
    setError(undefined);
    try {
      if (action.kind === "dispute") {
        await api(`/api/v1/disputes/${action.item.id}/status`, {
          method: "PUT",
          body: jsonBody({ status: action.status, reason, version: action.item.version }),
        });
        await disputes.refetch();
      } else if (action.kind === "exception") {
        await api(`/api/v1/reconciliation-exceptions/${action.item.id}/status`, {
          method: "PUT",
          body: jsonBody({ status: action.status, reason, version: action.item.version }),
        });
        await exceptions.refetch();
      } else {
        await api(`/api/v1/finance/accounting-periods/${action.item.id}/close`, {
          method: "POST",
          body: jsonBody({ status: "closed", reason, version: action.item.version }),
        });
        await periods.refetch();
      }
      setAction(undefined);
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function createPeriod(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const startsOn = String(form.get("startsOn"));
    const endsOn = String(form.get("endsOn"));
    setWorking(true);
    setError(undefined);
    try {
      await api("/api/v1/finance/accounting-periods", {
        method: "POST",
        body: jsonBody({ startsOn: `${startsOn}T00:00:00Z`, endsOn: `${endsOn}T00:00:00Z` }),
      });
      await periods.refetch();
      setPeriodOpen(false);
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  const disputeItems = disputes.data?.items ?? [];
  const exceptionItems = exceptions.data?.items ?? [];
  const periodItems = periods.data?.items ?? [];
  const activeDisputes = disputeItems.filter(
    (item) => !["won", "lost", "closed"].includes(item.status),
  ).length;
  const activeExceptions = exceptionItems.filter((item) =>
    ["open", "investigating"].includes(item.status),
  ).length;

  return (
    <section className="stack">
      <Card>
        <CardHeader
          title="Finance checks"
          description="Review payment differences, customer disputes, and locked accounting periods."
        />
        <div className="metrics-grid">
          {canManageFinance ? (
            <div className="metric">
              <span>Active disputes</span>
              <strong>{activeDisputes}</strong>
            </div>
          ) : null}
          {canRecordPayments ? (
            <div className="metric">
              <span>Payments to review</span>
              <strong>{activeExceptions}</strong>
            </div>
          ) : null}
          {canManagePeriods ? (
            <div className="metric">
              <span>Closed periods</span>
              <strong>{periodItems.filter((item) => item.status === "closed").length}</strong>
            </div>
          ) : null}
        </div>
        <Notice tone="info" title="Extra confirmation required">
          These actions change permanent finance records. Enter a fresh two-step verification code
          before continuing. Every change is recorded.
        </Notice>
        {sensitive.view}
      </Card>

      {error ? <ErrorPanel error={error} /> : null}

      {canManageFinance ? (
        <Card>
          <CardHeader
            title="Payment disputes"
            description="Track disputed customer payments from first report to final decision."
            actions={<AlertTriangle size={20} aria-hidden="true" />}
          />
          {disputes.error ? (
            <ErrorPanel error={disputes.error} />
          ) : disputeItems.length ? (
            <DataTable
              caption="Payment disputes"
              items={disputeItems}
              getKey={(item) => item.id}
              columns={[
                {
                  label: "Invoice",
                  render: (item) => (
                    <span>
                      <strong>{item.invoiceNumber}</strong>
                      <br />
                      <small>{item.customerName}</small>
                    </span>
                  ),
                },
                {
                  label: "Amount",
                  className: "numeric",
                  render: (item) => formatMoney(item.amountMinor, item.currency),
                },
                { label: "Reason", render: (item) => item.reason },
                { label: "Due", render: (item) => formatDate(item.responseDueAt) },
                { label: "Status", render: (item) => <StatusPill value={item.status} /> },
                {
                  label: "Action",
                  render: (item) =>
                    ["won", "lost", "closed"].includes(item.status) ? (
                      "Not available"
                    ) : (
                      <span className="inline">
                        <button
                          className="button secondary small"
                          type="button"
                          disabled={!sensitive.unlocked}
                          onClick={() =>
                            setAction({ kind: "dispute", item, status: "under_review" })
                          }
                        >
                          Review
                        </button>
                        <button
                          className="button primary small"
                          type="button"
                          disabled={!sensitive.unlocked}
                          onClick={() => setAction({ kind: "dispute", item, status: "won" })}
                        >
                          Won
                        </button>
                        <button
                          className="button danger small"
                          type="button"
                          disabled={!sensitive.unlocked}
                          onClick={() => setAction({ kind: "dispute", item, status: "lost" })}
                        >
                          Lost
                        </button>
                      </span>
                    ),
                },
              ]}
            />
          ) : (
            <EmptyState
              title="No disputes"
              description="Disputes recorded from a payment will appear here."
            />
          )}
        </Card>
      ) : null}

      {canRecordPayments ? (
        <Card>
          <CardHeader
            title="Payments to review"
            description="Review payments that do not match an invoice or expected amount."
            actions={<Scale size={20} aria-hidden="true" />}
          />
          {exceptions.error ? (
            <ErrorPanel error={exceptions.error} />
          ) : exceptionItems.length ? (
            <DataTable
              caption="Payment matching issues"
              items={exceptionItems}
              getKey={(item) => item.id}
              columns={[
                { label: "Invoice", render: (item) => item.invoiceNumber || "Organization-wide" },
                {
                  label: "Payment service",
                  render: (item) => (
                    <span>
                      {titleCase(item.provider)}
                      <br />
                      <small>{item.providerReference}</small>
                    </span>
                  ),
                },
                { label: "Type", render: (item) => titleCase(item.type) },
                { label: "Opened", render: (item) => formatDate(item.createdAt) },
                { label: "Status", render: (item) => <StatusPill value={item.status} /> },
                {
                  label: "Action",
                  render: (item) =>
                    ["resolved", "ignored"].includes(item.status) ? (
                      item.resolution || "Not available"
                    ) : (
                      <span className="inline">
                        <button
                          className="button secondary small"
                          type="button"
                          disabled={!sensitive.unlocked}
                          onClick={() =>
                            setAction({ kind: "exception", item, status: "investigating" })
                          }
                        >
                          Investigate
                        </button>
                        <button
                          className="button primary small"
                          type="button"
                          disabled={!sensitive.unlocked}
                          onClick={() => setAction({ kind: "exception", item, status: "resolved" })}
                        >
                          <CheckCircle2 size={15} /> Resolve
                        </button>
                        <button
                          className="button ghost small"
                          type="button"
                          disabled={!sensitive.unlocked}
                          onClick={() => setAction({ kind: "exception", item, status: "ignored" })}
                        >
                          Ignore
                        </button>
                      </span>
                    ),
                },
              ]}
            />
          ) : (
            <EmptyState
              title="All payments match"
              description="Payments that need a decision will appear here."
            />
          )}
        </Card>
      ) : null}

      {canManagePeriods ? (
        <Card>
          <CardHeader
            title="Accounting periods"
            description="Close a finished date range to prevent new backdated finance entries."
            actions={
              <button
                className="button secondary small"
                type="button"
                disabled={!sensitive.unlocked}
                onClick={() => {
                  setError(undefined);
                  setPeriodOpen(true);
                }}
              >
                <CalendarClock size={16} /> New period
              </button>
            }
          />
          {periods.error ? (
            <ErrorPanel error={periods.error} />
          ) : periodItems.length ? (
            <DataTable
              caption="Accounting periods"
              items={periodItems}
              getKey={(item) => item.id}
              columns={[
                { label: "Starts", render: (item) => formatDate(item.startsOn) },
                { label: "Ends", render: (item) => formatDate(item.endsOn) },
                { label: "Status", render: (item) => <StatusPill value={item.status} /> },
                { label: "Closed", render: (item) => formatDate(item.closedAt) },
                {
                  label: "Action",
                  render: (item) =>
                    item.status === "open" ? (
                      <button
                        className="button danger small"
                        type="button"
                        disabled={!sensitive.unlocked}
                        onClick={() => setAction({ kind: "period", item, status: "closed" })}
                      >
                        Close period
                      </button>
                    ) : (
                      item.closeReason || "Not available"
                    ),
                },
              ]}
            />
          ) : (
            <EmptyState
              title="No accounting periods"
              description="Create a date range when you are ready to begin a regular finance close."
            />
          )}
        </Card>
      ) : null}

      <Modal
        open={periodOpen}
        onClose={() => setPeriodOpen(false)}
        title="New accounting period"
        description="Periods cannot overlap. A period can only be closed after its final date has passed."
      >
        {error ? <ErrorPanel error={error} /> : null}
        <form className="form-stack" onSubmit={createPeriod}>
          <Field label="Starts on">
            <input name="startsOn" type="date" required />
          </Field>
          <Field label="Ends on">
            <input name="endsOn" type="date" required />
          </Field>
          <FormActions
            submitting={working}
            disabled={!sensitive.unlocked}
            submitLabel="Create period"
            onCancel={() => setPeriodOpen(false)}
          />
        </form>
      </Modal>

      <ReasonDialog
        open={Boolean(action)}
        onClose={() => setAction(undefined)}
        onSubmit={transition}
        title={action ? `${titleCase(action.status)} ${action.kind}` : "Update finance record"}
        description={
          action?.kind === "period"
            ? "Closing cannot be undone and prevents new finance entries dated inside this period."
            : "Explain this decision for the permanent audit trail."
        }
        initialReason={
          action?.kind === "period" ? "Period reviewed and approved for financial close." : ""
        }
        minLength={action?.kind === "period" ? 10 : 5}
        submitLabel={
          action?.kind === "period"
            ? "Close accounting period"
            : `Mark ${titleCase(action?.status ?? "updated")}`
        }
        tone={action?.status === "lost" || action?.status === "closed" ? "danger" : "primary"}
        submitting={working}
        disabled={!sensitive.unlocked}
      />
    </section>
  );
}
